import React from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import InputGroup from '../InputGroup';
import ReactGoogleAutocomplete from '../InputFields/GoogleMapsAddressField';
import OptInsAndButtonGroup from './OptInsAndButtonGroup';

import submissionErrorHandler from '../../utils/submissionErrorHandler';
import { getCachedFullName } from '../../utils/formCaching/helpers';
import { copyFor, capitalization } from '../../copy/copy';
import validate from './validations';

const DetailsFormContainer = styled.form`
    flex: 1;
    padding: 0;
    display: flex;
    flex-direction: column;
`;

const Row = styled.div`
    margin: 0 -0.5em;
    transition: opacity 0.6s;

    &.focused {
        opacity: 0;

        &.row-wrapper-2 {
            opacity: 1;

            textarea {
                height: 190px;
            }
        }
    }
`;

const FullRow = styled.div`
    width: 100%;
    padding: 0 8px;
`;

class PetitionForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            valid: false,
            errors: submissionErrorHandler(props.currentFormValues, validate)
        };
        this.clearSubmitErrors = this.clearSubmitErrors.bind(this);
        this.setSubmitErrors = this.setSubmitErrors.bind(this);
        this.setValidState = this.setValidState.bind(this);
    }

    componentDidMount() {
        this.setState({ errors: [] });
    }

    setValidState(bool) {
        this.setState({ valid: bool });
    }

    setSubmitErrors(errors) {
        this.setState({ errors });
    }

    checkForBlurError(name) {
        const errors = submissionErrorHandler(this.props.currentFormValues, validate);
        if (errors.includes(name) && !this.state.errors.includes(name))
            this.setState({ errors: [...this.state.errors, name] });
    }

    clearSubmitErrors(fieldName) {
        if (fieldName && this.state.errors.includes(fieldName)) {
            this.setState({ errors: this.state.errors.filter(e => e !== fieldName) });
        }
    }
    render() {
        return (
            <DetailsFormContainer autocomplete="on">
                <Row className="row-wrapper-1">
                    <FullRow>
                        <Field
                            name="fullName"
                            submissionError={this.state.errors.includes('fullName')}
                            onBlur={() => this.checkForBlurError('fullName')}
                            clearSubmitErrors={this.clearSubmitErrors}
                            component={InputGroup}
                            type="text"
                            label={copyFor(
                                'formFields',
                                'fullName',
                                'petition',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor('placeholders', 'fullName', 'petition')}
                            paddingAdjustment={-3}
                            onFocus={this.props.onInitiated}
                        />
                    </FullRow>
                    <FullRow>
                        <Field
                            name="email"
                            onBlur={() => this.checkForBlurError('email')}
                            submissionError={this.state.errors.includes('email')}
                            clearSubmitErrors={this.clearSubmitErrors}
                            component={InputGroup}
                            type="email"
                            label={copyFor(
                                'formFields',
                                'emailAddress',
                                'petition',
                                capitalization.eachWord
                            )}
                            placeholder={copyFor(
                                'placeholders',
                                'emailAddress',
                                'petition'
                            )}
                            paddingAdjustment={-3}
                            onFocus={this.props.onInitiated}
                        />
                    </FullRow>
                    <FullRow onBlur={() => this.checkForBlurError('address')}>
                        {this.props.userInfoType === 'address' && (
                            <ReactGoogleAutocomplete
                                submissionError={this.state.errors.includes('address')}
                                clearSubmitErrors={this.clearSubmitErrors}
                                formName={['petitionDetailsForm', 'google_autocomplete']}
                                fields={['address_components', 'formatted_address']}
                                types={['address']}
                                setValidState={this.setValidState}
                                paddingAdjustment={-3}
                                onFocus={this.props.onInitiated}
                            />
                        )}
                        {this.props.userInfoType !== 'address' && (
                            <Field
                                name={this.props.userInfoType}
                                submissionError={this.state.errors.includes(
                                    this.props.userInfoType
                                )}
                                clearSubmitErrors={this.clearSubmitErrors}
                                component={InputGroup}
                                type={this.props.userInfoType}
                                label={copyFor(
                                    'formFields',
                                    this.props.userInfoType,
                                    'petition',
                                    capitalization.eachWord
                                )}
                                placeholder={copyFor(
                                    'placeholders',
                                    this.props.userInfoType,
                                    'petition'
                                )}
                                paddingAdjustment={-3}
                                onFocus={this.props.onInitiated}
                            />
                        )}
                    </FullRow>
                </Row>
                <OptInsAndButtonGroup
                    currentFormValues={this.props.currentFormValues}
                    setValidState={this.setValidState}
                    clearSubmitErrors={this.clearSubmitErrors}
                    setSubmitErrors={this.setSubmitErrors}
                    buttonText={this.props.buttonText}
                    onSubmit={this.props.onSubmit}
                    hasABOptIn={this.props.hasABOptIn}
                    invalid={
                        this.props.invalid ||
                        (this.props.userInfoType === 'address' && !this.state.valid)
                    }
                    isCare2={this.props.isCare2}
                    isEU={!!this.props.endUserLocation.isEU}
                    isSubmitting={this.props.isSubmitting}
                    partnerOptIn={this.props.showPartnerOptIn}
                    partnerOptInText={this.props.partnerOptInText}
                    subscriptionOptInText={this.props.subscriptionOptInText}
                    validate={validate}
                />
            </DetailsFormContainer>
        );
    }
}

PetitionForm.propTypes = {
    buttonText: PropTypes.string.isRequired,
    currentFormValues: PropTypes.object,
    endUserLocation: PropTypes.object.isRequired,
    onSubmit: PropTypes.func.isRequired,
    hasABOptIn: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    isCare2: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    onInitiated: PropTypes.func.isRequired,
    partnerOptInText: PropTypes.string,
    showPartnerOptIn: PropTypes.bool.isRequired,
    subscriptionOptInText: PropTypes.string,
    userInfoType: PropTypes.string.isRequired
};

export default compose(
    connect(state => {
        let userInfoType = state.widget.activeAction.requiredUserInfo || 'address';
        var hasABOptIn = !state.widget.activeAction.hideSubscriptionOptIn;
        var showPartnerOptIn = state.widget.activeAction.hasPartnerOptIn;
        let initialValues = {
            getUpdates: !(state.widget.endUserLocation || {}).isEU && hasABOptIn,
            partnerOptIn: !(state.widget.endUserLocation || {}).isEU && showPartnerOptIn,
            fullName: getCachedFullName(state),
            email: state.cachedFormData.email || ''
        };
        if (userInfoType === 'postalCode')
            initialValues.postalCode = state.cachedFormData.postalCode || '';
        if (userInfoType === 'phone')
            initialValues.phone = state.cachedFormData.phone || '';
        if (userInfoType === 'address')
            initialValues.address = state.cachedFormData.address || '';

        return {
            initialValues,
            localRepFullName:
                state.localRep.localRepData.representative !== undefined
                    ? state.localRep.localRepData.representative[0].fullName
                    : '',
            endUserLocation: state.widget.endUserLocation || {},
            buttonText:
                state.widget.activeAction.customButtonText ||
                copyFor('buttons', 'submit', 'petition', capitalization.eachWord),
            hasABOptIn,
            isCare2: !!state.widget.activeAction.care2Id,
            partnerOptInText:
                state.widget.activeAction.partnerOptInText ||
                (state.widget.activeAction.partner || {}).name,
            showPartnerOptIn,
            subscriptionOptInText: state.widget.activeAction.subscriptionOptInText,
            userInfoType,
            currentFormValues: (state.form['petitionDetailsForm'] || {}).values
        };
    }),
    reduxForm({
        form: 'petitionDetailsForm',
        destroyOnUnmount: true,
        enableReinitialize: false,
        validate
    })
)(PetitionForm);
